import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Buildings, Users, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLINIC_TYPES = [
  { key: "pharmacy", label: "Pharmacy" },
  { key: "vet", label: "Veterinary Clinic" },
  { key: "dental", label: "Dental Practice" },
  { key: "skin_care", label: "Skin Care / Dermatology" },
  { key: "cosmetic_tattoos", label: "Cosmetic Tattoo / Aesthetic" },
  { key: "gp_clinic", label: "GP Clinic" },
  { key: "specialist_clinic", label: "Specialist Clinic" },
  { key: "other", label: "Other" },
];

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [tab, setTab] = useState("login");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  // Register tab — sub-mode (create vs join)
  const [regMode, setRegMode] = useState("create");

  // Shared register state
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regPhone, setRegPhone] = useState("");

  // Create-org state
  const [clinicName, setClinicName] = useState("");
  const [clinicType, setClinicType] = useState("vet");
  const [addrLine1, setAddrLine1] = useState("");
  const [addrLine2, setAddrLine2] = useState("");
  const [addrSuburb, setAddrSuburb] = useState("");
  const [addrState, setAddrState] = useState("NSW");
  const [addrPostcode, setAddrPostcode] = useState("");
  const [contactName, setContactName] = useState("");

  // Join-org state
  const [orgCode, setOrgCode] = useState("");
  const [foundOrg, setFoundOrg] = useState(null);
  const [searchingOrg, setSearchingOrg] = useState(false);

  // Auto-fill from ?join=NNNN-NNNN URL param (QR code path)
  useEffect(() => {
    const joinCode = searchParams.get("join");
    if (joinCode) {
      setTab("register");
      setRegMode("join");
      setOrgCode(joinCode.toUpperCase());
      // Auto-trigger search
      (async () => {
        try {
          const { data } = await axios.get(`${API}/orgs/search?code=${encodeURIComponent(joinCode)}`);
          if (data.found) setFoundOrg(data.org);
        } catch { /* ignore */ }
      })();
    }
  }, [searchParams]);

  const resetMessages = () => { setError(""); setSuccess(""); };

  const handleLogin = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
    const result = await login(loginEmail, loginPassword);
    setLoading(false);
    if (result.success) navigate("/");
    else setError(result.error);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setSuccess(data.message || "If an account exists for that email, a reset link has been sent.");
      setForgotEmail("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not process request. Please try again.");
    }
    setLoading(false);
  };

  const handleSearchOrg = async () => {
    if (!orgCode || orgCode.length < 4) { setError("Enter the org code your clinic admin gave you"); return; }
    resetMessages();
    setSearchingOrg(true);
    try {
      const { data } = await axios.get(`${API}/orgs/search?code=${encodeURIComponent(orgCode)}`);
      if (data.found) { setFoundOrg(data.org); }
      else { setFoundOrg(null); setError("No active clinic found with that code"); }
    } catch (e) {
      setError("Search failed");
    }
    setSearchingOrg(false);
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register-org`, {
        email: regEmail, password: regPassword, name: regName, phone: regPhone,
        clinic_name: clinicName, clinic_type: clinicType,
        address: { label: "Main Clinic", street_line1: addrLine1, street_line2: addrLine2, suburb: addrSuburb, state: addrState, postcode: addrPostcode, contact_name: contactName || regName, contact_phone: regPhone },
      });
      setSuccess(`Application submitted! We'll review ${clinicName} and notify you. You'll receive your organization code once approved.`);
      // reset form
      setRegName(""); setRegEmail(""); setRegPassword(""); setRegPhone(""); setClinicName(""); setAddrLine1(""); setAddrLine2(""); setAddrSuburb(""); setAddrPostcode(""); setContactName("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Application failed");
    }
    setLoading(false);
  };

  const handleJoinOrg = async (e) => {
    e.preventDefault();
    resetMessages();
    if (!foundOrg) { setError("Search for your clinic by code first"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register-join`, {
        email: regEmail, password: regPassword, name: regName, phone: regPhone, org_code: foundOrg.code,
      });
      setSuccess(`Join request sent to ${foundOrg.name}. The clinic admin will review and approve.`);
      setRegName(""); setRegEmail(""); setRegPassword(""); setRegPhone(""); setOrgCode(""); setFoundOrg(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Request failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      {/* Left panel - hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-zinc-950">
        <img src="https://images.unsplash.com/photo-1657551734497-30a09e6567bc?w=1200&q=80" alt="Pharmacist" className="object-cover w-full h-full opacity-40" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tighter leading-none text-white">AMCAL NEWMARKET</h1>
          <p className="font-[Chivo] text-xl font-semibold text-zinc-300 mt-2 tracking-tight">COMPOUNDING PHARMACY</p>
          <p className="text-zinc-400 mt-4 text-sm max-w-md leading-relaxed">Order compounded medications with ease. Browse our catalog, submit prescriptions, and track your orders in real-time.</p>
          <div className="flex gap-8 mt-8">
            <div><p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">TURNAROUND</p><p className="text-white font-semibold mt-1">2-3 Days</p></div>
            <div><p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">CUT-OFF</p><p className="text-white font-semibold mt-1">12:00 PM Daily</p></div>
            <div><p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">PHONE</p><p className="text-white font-semibold mt-1">07-3356-3964</p></div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0055FF] flex items-center justify-center"><span className="text-white font-black text-lg font-[Chivo]">A</span></div>
              <div>
                <h1 className="font-[Chivo] font-bold text-zinc-950 text-lg tracking-tight">AMCAL NEWMARKET</h1>
                <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Compounding Pharmacy</p>
              </div>
            </div>
          </div>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); resetMessages(); }} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-zinc-100">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="auth-error">{error}</div>}
            {success && <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-start gap-2" data-testid="auth-success"><CheckCircle size={18} weight="fill" className="shrink-0 mt-0.5" /><span>{success}</span></div>}

            {/* SIGN IN */}
            <TabsContent value="login" className="mt-6">
              {forgotMode ? (
                <form onSubmit={handleForgot} className="space-y-4" data-testid="forgot-form">
                  <div>
                    <p className="font-[Chivo] font-bold text-zinc-950">Reset your password</p>
                    <p className="text-sm text-zinc-500 mt-1">Enter your account email and we'll send you a link to reset your password.</p>
                  </div>
                  <div>
                    <Label htmlFor="forgot-email" className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                    <Input id="forgot-email" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required className="mt-1.5 rounded-none" data-testid="forgot-email-input" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-[#0055FF] text-white font-semibold rounded-none px-6 py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98] disabled:opacity-50" data-testid="forgot-submit-button">
                    {loading ? "SENDING..." : "SEND RESET LINK"}
                  </button>
                  <button type="button" onClick={() => { setForgotMode(false); resetMessages(); }} className="w-full text-center text-sm text-zinc-500 hover:text-zinc-950 transition-colors" data-testid="back-to-login-button">
                    Back to sign in
                  </button>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                    <Input id="login-email" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required className="mt-1.5 rounded-none" data-testid="login-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Password</Label>
                    <Input id="login-password" type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required className="mt-1.5 rounded-none" data-testid="login-password-input" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-[#0055FF] text-white font-semibold rounded-none px-6 py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98] disabled:opacity-50" data-testid="login-submit-button">
                    {loading ? "SIGNING IN..." : "SIGN IN"}
                  </button>
                  <button type="button" onClick={() => { setForgotMode(true); resetMessages(); }} className="w-full text-center text-sm text-[#0055FF] hover:underline" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </form>
              )}
            </TabsContent>

            {/* REGISTER */}
            <TabsContent value="register" className="mt-6">
              <div className="grid grid-cols-2 gap-2 mb-4" data-testid="register-mode-toggle">
                <button
                  type="button"
                  onClick={() => { setRegMode("create"); resetMessages(); }}
                  className={`p-3 border text-left transition-colors ${regMode === "create" ? "border-[#0055FF] bg-blue-50" : "border-zinc-200 hover:border-zinc-300"}`}
                  data-testid="register-mode-create"
                >
                  <Buildings size={18} className={regMode === "create" ? "text-[#0055FF]" : "text-zinc-400"} weight={regMode === "create" ? "fill" : "regular"} />
                  <p className="font-[Chivo] text-sm font-bold text-zinc-950 mt-1">Create new clinic</p>
                  <p className="text-xs text-zinc-500 mt-0.5">First time registering your clinic</p>
                </button>
                <button
                  type="button"
                  onClick={() => { setRegMode("join"); resetMessages(); }}
                  className={`p-3 border text-left transition-colors ${regMode === "join" ? "border-[#0055FF] bg-blue-50" : "border-zinc-200 hover:border-zinc-300"}`}
                  data-testid="register-mode-join"
                >
                  <Users size={18} className={regMode === "join" ? "text-[#0055FF]" : "text-zinc-400"} weight={regMode === "join" ? "fill" : "regular"} />
                  <p className="font-[Chivo] text-sm font-bold text-zinc-950 mt-1">Join existing clinic</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Use the org code from your clinic admin</p>
                </button>
              </div>

              {regMode === "create" ? (
                <form onSubmit={handleCreateOrg} className="space-y-3" data-testid="create-org-form">
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mb-1">YOUR DETAILS</p>
                  <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Full Name</Label><Input value={regName} onChange={e => setRegName(e.target.value)} required className="mt-1.5 rounded-none" data-testid="create-name-input" /></div>
                  <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Email</Label><Input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} required className="mt-1.5 rounded-none" data-testid="create-email-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Password</Label><Input type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} required minLength={6} className="mt-1.5 rounded-none" data-testid="create-password-input" /></div>
                    <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Phone</Label><Input value={regPhone} onChange={e => setRegPhone(e.target.value)} className="mt-1.5 rounded-none" data-testid="create-phone-input" /></div>
                  </div>

                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-4 mb-1">CLINIC DETAILS</p>
                  <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Clinic Name</Label><Input value={clinicName} onChange={e => setClinicName(e.target.value)} required placeholder="e.g. Smith Veterinary Clinic" className="mt-1.5 rounded-none" data-testid="create-clinic-name-input" /></div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Clinic Type</Label>
                    <Select value={clinicType} onValueChange={setClinicType}>
                      <SelectTrigger className="mt-1.5 rounded-none" data-testid="create-clinic-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CLINIC_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-4 mb-1">DELIVERY ADDRESS</p>
                  <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Street Address</Label><Input value={addrLine1} onChange={e => setAddrLine1(e.target.value)} required placeholder="123 Pet St" className="mt-1.5 rounded-none" data-testid="create-addr-line1-input" /></div>
                  <div><Input value={addrLine2} onChange={e => setAddrLine2(e.target.value)} placeholder="Unit / floor (optional)" className="rounded-none" data-testid="create-addr-line2-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Suburb</Label><Input value={addrSuburb} onChange={e => setAddrSuburb(e.target.value)} required className="mt-1.5 rounded-none" data-testid="create-addr-suburb-input" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">State</Label>
                        <Select value={addrState} onValueChange={setAddrState}>
                          <SelectTrigger className="mt-1.5 rounded-none" data-testid="create-addr-state-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{AU_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Postcode</Label><Input value={addrPostcode} onChange={e => setAddrPostcode(e.target.value)} required maxLength={4} className="mt-1.5 rounded-none" data-testid="create-addr-postcode-input" /></div>
                    </div>
                  </div>
                  <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Contact at this address</Label><Input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Defaults to your name" className="mt-1.5 rounded-none" data-testid="create-contact-name-input" /></div>

                  <button type="submit" disabled={loading} className="w-full bg-[#0055FF] text-white font-semibold rounded-none px-6 py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98] disabled:opacity-50 mt-2" data-testid="create-org-submit">
                    {loading ? "SUBMITTING..." : "SUBMIT FOR APPROVAL"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleJoinOrg} className="space-y-3" data-testid="join-org-form">
                  <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mb-1">CLINIC CODE</p>
                  <div className="flex gap-2">
                    <Input value={orgCode} onChange={e => { setOrgCode(e.target.value.toUpperCase()); setFoundOrg(null); }} placeholder="e.g. 1024-5567" className="rounded-none flex-1 font-mono" data-testid="join-code-input" />
                    <button type="button" onClick={handleSearchOrg} disabled={searchingOrg} className="px-4 py-2 text-sm font-semibold bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 transition-colors disabled:opacity-50" data-testid="join-search-button">
                      {searchingOrg ? "..." : "Search"}
                    </button>
                  </div>
                  {foundOrg && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 flex items-start gap-2" data-testid="found-org">
                      <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-zinc-950">{foundOrg.name}</p>
                        <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-0.5">{foundOrg.type.replace("_", " ")}</p>
                      </div>
                    </div>
                  )}

                  {foundOrg && (
                    <>
                      <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-4 mb-1">YOUR DETAILS</p>
                      <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Full Name</Label><Input value={regName} onChange={e => setRegName(e.target.value)} required className="mt-1.5 rounded-none" data-testid="join-name-input" /></div>
                      <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Email</Label><Input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} required className="mt-1.5 rounded-none" data-testid="join-email-input" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Password</Label><Input type="password" value={regPassword} onChange={e => setRegPassword(e.target.value)} required minLength={6} className="mt-1.5 rounded-none" data-testid="join-password-input" /></div>
                        <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Phone</Label><Input value={regPhone} onChange={e => setRegPhone(e.target.value)} className="mt-1.5 rounded-none" data-testid="join-phone-input" /></div>
                      </div>

                      <button type="submit" disabled={loading} className="w-full bg-[#0055FF] text-white font-semibold rounded-none px-6 py-3 hover:bg-[#0044CC] transition-colors active:scale-[0.98] disabled:opacity-50 mt-2" data-testid="join-org-submit">
                        {loading ? "SENDING..." : `REQUEST TO JOIN ${foundOrg.name.toUpperCase()}`}
                      </button>
                    </>
                  )}
                </form>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
